import { trackEvent } from '@/utils/analytics'
import { Star, CopyIcon, Download, Loader2, Check, RefreshCw, Search } from 'lucide-react'
import { useIsInternalUser } from '@/hooks/useInternalUser'
import { useTranslation } from 'react-i18next'
import { apiService, Message } from '../../../service/api'
import { useState, useRef } from 'react'
import { toast } from 'sonner'
import { latexToPlainText } from '@/utils/katexMathPreprocessor'
import TooltipOverlay from '@/components/TooltipOverlay'
import { FeedbackModal } from '@/features/chatV2/components/messages/FeedbackModal'
import {
  downloadMessage,
  triggerBlobDownload,
} from '@/features/chatV2/api/chatApiV2'
import type { DownloadFormat } from '@/features/chatV2/api/chatApiV2'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { VersionNavigator } from '@/features/chatV2/components/messages/VersionNavigator'
import type { VersionInfo } from '@/features/chatV2/types'

// File format icons – all paths, no <text> elements
const PdfIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M14 2v6h6" fill="#E5E5E5" stroke="#1A1A1A" strokeWidth="1.2" strokeLinejoin="round"/>
    <rect x="2" y="13" width="16" height="7" rx="1.5" fill="#1A1A1A"/>
    <path d="M5 15v4m0-4h1.3c1.4 0 1.4 2.2 0 2.2H5" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 15v4m0-4h.8c2 0 2 4 0 4H9" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.5 15v4m0-4H16m-2.5 2h2" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const DocxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M14 2v6h6" fill="#E5E5E5" stroke="#1A1A1A" strokeWidth="1.2" strokeLinejoin="round"/>
    <rect x="1" y="13" width="18" height="7" rx="1.5" fill="#1A1A1A"/>
    <path d="M3.2 15v4m0-4h.6c1.8 0 1.8 4 0 4h-.6" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
    <ellipse cx="8" cy="17" rx="1.2" ry="2" stroke="#fff" strokeWidth="0.9" fill="none"/>
    <path d="M12.8 15.2c-1-.4-2.2.2-2.2 1.8s1.2 2.2 2.2 1.8" stroke="#fff" strokeWidth="0.9" strokeLinecap="round" fill="none"/>
    <path d="M14.5 15l2.5 4m0-4l-2.5 4" stroke="#fff" strokeWidth="0.9" strokeLinecap="round"/>
  </svg>
)

const MarkdownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="3" fill="#F5F5F5" stroke="#1A1A1A" strokeWidth="1.2"/>
    <path d="M5 16V8l3 4 3-4v8" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 10v6m0 0l-2.5-2.5M17 16l2.5-2.5" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)


const DOWNLOAD_FORMATS: { format: DownloadFormat; label: string; icon: JSX.Element }[] = [
  { format: 'pdf', label: 'PDF', icon: <PdfIcon /> },
  { format: 'md', label: 'Markdown', icon: <MarkdownIcon /> },
  { format: 'docx', label: 'DOCX', icon: <DocxIcon /> },
  
]

interface AnswerActionsProps {
  message: Message
  handleFeedbackSubmitted: (messageId: string, rating: number | undefined) => void
  onRegenerate?: () => void
  isRegenerateDisabled?: boolean
  versionInfo?: VersionInfo | null
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  /** The preceding user question text for Q&A context in rating emails */
  userQuestion?: string
}

const AnswerActions = ({
  message,
  handleFeedbackSubmitted,
  onRegenerate,
  isRegenerateDisabled = false,
  versionInfo,
  onNavigateVersion,
  userQuestion,
}: AnswerActionsProps) => {
  const { t } = useTranslation('chat')
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const screenshotPromiseRef = useRef<Promise<string | null>>(Promise.resolve(null))
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadSuccess, setDownloadSuccess] = useState(false)
  const isInternalUser = useIsInternalUser()

  const handleRateClick = () => {
    if (isLoading || !message.id) return
    // Fire-and-forget: start capture, open modal immediately (no lag)
    // Filter out modal overlay (data-screenshot-exclude) + tldraw canvas artifacts
    screenshotPromiseRef.current = import('html-to-image')
      .then(({ toJpeg }) =>
        toJpeg(document.body, {
          quality: 0.6,
          backgroundColor: '#ffffff',
          filter: (node: HTMLElement) => {
            if (node.dataset?.screenshotExclude !== undefined) return false
            const cl = node.classList
            if (!cl) return true
            if (cl.contains('tl-background') || cl.contains('tl-grid') || cl.contains('tl-canvas')) return false
            return true
          },
        }),
      )
      .catch(() => null)
    setShowFeedbackModal(true)
  }

  const handleFeedbackSubmit = async (feedbackText: string, rating: number | null) => {
    if (isLoading || rating === null) return
    if (!message.id || !message.conversation_id) {
      // FS-490: guard against missing identifiers (e.g. V2 stream
      // synthetic regression) so we don't POST `/conversations/undefined/...`.
      // Surface to the user instead of failing silently.
      toast.error(t('toasts.feedback_error', { error: 'missing message context' }))
      setShowFeedbackModal(false)
      return
    }
    setIsLoading(true)
    try {
      const screenshot = await screenshotPromiseRef.current
      await apiService.addStarRating(
        message.conversation_id,
        message.id,
        rating,
        feedbackText || undefined,
        screenshot || undefined,
        userQuestion,
        message.content || undefined
      )
      handleFeedbackSubmitted(message.id, rating)
      trackEvent('chat-feedback_submitted', {
        message_id: message.id,
        conversation_id: message.conversation_id,
        rating,
      })
      setShowFeedbackModal(false)
      toast.success(t('toasts.feedback_success'))
    } catch (error) {
      toast.error(t('toasts.feedback_error', { error: String(error) }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(latexToPlainText(message.content))
      trackEvent('chat-message_copied', {
        message_id: message.id,
        content_length: message.content.length,
      })
      toast.success(t('toasts.copy_success'))
    } catch {
      toast.error(t('toasts.copy_error'))
    }
  }

  const handleDownload = async (format: DownloadFormat) => {
    if (downloadLoading || !message.conversation_id || !message.id) return

    setDownloadLoading(true)
    setDownloadSuccess(false)
    try {
      const { blob, filename } = await downloadMessage(
        message.conversation_id,
        message.id,
        format
      )
      triggerBlobDownload(blob, filename)
      setDownloadSuccess(true)
      toast.success(t('toasts.downloaded', { filename }))
      setTimeout(() => setDownloadSuccess(false), 2000)
    } catch {
      toast.error(t('toasts.download_error'))
    } finally {
      setDownloadLoading(false)
    }
  }

  const hasRating = message.rating !== undefined && message.rating !== null

  return (
    <>
      <div className="flex gap-3 items-center">
        <TooltipOverlay message={hasRating ? `Rated ${message.rating}/5 - Click to update` : 'Rate this response'} side="top">
          <button
            onClick={handleRateClick}
            disabled={isLoading || !message.id}
            aria-label={hasRating ? `Rated ${message.rating}/5 - Click to update` : 'Rate this response'}
            className={`flex items-center gap-1 transition-all cursor-pointer ${
              hasRating
                ? 'text-yellow-500 opacity-100 hover:opacity-80'
                : 'text-primary opacity-60 hover:opacity-100'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Star
              size={17}
              fill={hasRating ? 'currentColor' : 'none'}
            />
            {hasRating && (
              <span className="text-xs font-medium">{message.rating}/5</span>
            )}
          </button>
        </TooltipOverlay>
        <TooltipOverlay message="Copy to clipboard" side="top">
          <button
            onClick={handleCopy}
            aria-label="Copy to clipboard"
            className="text-primary opacity-60 hover:opacity-100 transition-all cursor-pointer"
          >
            <CopyIcon size={17} />
          </button>
        </TooltipOverlay>
        {versionInfo && onNavigateVersion && (
          <VersionNavigator versionInfo={versionInfo} onNavigate={onNavigateVersion} />
        )}
        {onRegenerate && (
          <TooltipOverlay message={t('actions.retry_response')} side="top">
            <button
              onClick={onRegenerate}
              aria-label={t('actions.retry_response')}
              disabled={isRegenerateDisabled}
              className="text-primary opacity-60 hover:opacity-100 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <RefreshCw size={17} />
            </button>
          </TooltipOverlay>
        )}
        {/* AGNT-685 forensics deep-link. Mirrors the chatV2 button in
            AssistantMessage.tsx — only renders when the backend echoed
            an x-correlation-id header AND the signed-in user is
            Intuigence staff (a customer account never sees the trace).
            Engineer-facing; opens in a new tab so the chat session is
            preserved. */}
        {isInternalUser && message.correlation_id && (
          <TooltipOverlay message="Open agent trace (engineer debug)" side="top">
            <a
              href={`/kg-trace/${encodeURIComponent(message.correlation_id)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="kg-trace-link"
              aria-label="Open agent trace (engineer debug)"
              className="text-primary opacity-60 hover:opacity-100 transition-all cursor-pointer"
            >
              <Search size={17} aria-hidden="true" />
            </a>
          </TooltipOverlay>
        )}
        {message.content?.trim() && (
          <DropdownMenu>
            <TooltipOverlay message="Download as file" side="top">
              <DropdownMenuTrigger asChild>
                <button
                  disabled={downloadLoading}
                  aria-label="Download as file"
                  className="transition-all cursor-pointer text-primary opacity-60 hover:opacity-100 disabled:opacity-30"
                >
                  {downloadLoading ? (
                    <Loader2 size={17} className="animate-spin" />
                  ) : downloadSuccess ? (
                    <Check size={17} className="text-green-500" />
                  ) : (
                    <Download size={17} />
                  )}
                </button>
              </DropdownMenuTrigger>
            </TooltipOverlay>
            <DropdownMenuContent align="start" className="w-48">
              {DOWNLOAD_FORMATS.map(({ format, label, icon }) => (
                <DropdownMenuItem
                  key={format}
                  onClick={() => handleDownload(format)}
                  className="cursor-pointer"
                >
                  <span className="mr-2 flex items-center">{icon}</span>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        isLoading={isLoading}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        initialRating={message.rating ?? null}
      />
    </>
  )
}

export default AnswerActions
